import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import dataManager from '../utils/dataManager';
import useLoadMore from '../utils/useLoadMore';
import ImageWithFallback from './ImageWithFallback';
import LoadMoreButton from './LoadMoreButton';

const ExerciseList = () => {
  const [exercises, setExercises] = useState([]);
  const [meta, setMeta] = useState({ body_parts: [], equipments: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [activeBodyPart, setActiveBodyPart] = useState('');
  const [activeEquipment, setActiveEquipment] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // 初始加载：全量列表 + 筛选元数据
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const [list, metaData] = await Promise.all([
          dataManager.getExercises(),
          dataManager.getExerciseMeta(),
        ]);
        if (!cancelled) {
          setExercises(Array.isArray(list) ? list : []);
          setMeta(metaData || { body_parts: [], equipments: [] });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || '加载失败，请稍后重试');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // 搜索防抖
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // 客户端过滤（全量列表已缓存，过滤即时响应）
  const filteredExercises = useMemo(() => {
    const keyword = search.toLowerCase();
    return exercises.filter((ex) => {
      if (activeBodyPart && ex.body_part !== activeBodyPart) return false;
      if (activeEquipment && ex.equipment !== activeEquipment) return false;
      if (keyword && !(ex.name || '').toLowerCase().includes(keyword)) return false;
      return true;
    });
  }, [exercises, activeBodyPart, activeEquipment, search]);

  // 渐进式分页：首批 24 个，"加载更多"追加，筛选变化时自动重置
  const {
    visibleItems: visibleExercises,
    hasMore,
    remaining,
    loadMore,
  } = useLoadMore(
    filteredExercises,
    24,
    `${activeBodyPart}|${activeEquipment}|${search}`
  );

  // 当前部位下可用的器材（让器材筛选跟随部位变化，避免组合出空结果）
  const availableEquipments = useMemo(() => {
    if (!activeBodyPart) return meta.equipments;
    const inPart = new Set(
      exercises.filter((ex) => ex.body_part === activeBodyPart).map((ex) => ex.equipment)
    );
    return meta.equipments.filter((eq) => inPart.has(eq.equipment));
  }, [meta, exercises, activeBodyPart]);

  const handleSelectBodyPart = (bodyPart) => {
    setActiveBodyPart(bodyPart);
    setActiveEquipment('');
  };

  // 打开详情弹窗
  const openDetail = async (exercise) => {
    setSelectedId(exercise.id);
    setDetail(null);
    setDetailLoading(true);
    try {
      const data = await dataManager.getExerciseDetail(exercise.id);
      setDetail(data);
    } catch (err) {
      setDetail({ error: err.message || '加载详情失败' });
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedId(null);
    setDetail(null);
  };

  // 弹窗打开时锁定背景滚动
  useEffect(() => {
    if (selectedId === null) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [selectedId]);

  return (
    <div className="max-w-6xl mx-auto">
      {/* 页面标题 */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-serif font-bold text-sage-800">健身动作库</h1>
        <p className="text-sm sm:text-base text-sage-600 mt-1">
          1324 个训练动作，含动画演示与中文步骤，作为瑜伽练习的力量补充
        </p>
      </div>

      {/* 搜索框 */}
      <div className="relative mb-4">
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
          <svg className="h-5 w-5 text-sage-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="搜索动作英文名，如 squat / plank"
          className="w-full pl-11 pr-4 py-3 text-base border-2 border-sage-300 rounded-xl focus:outline-none focus:ring-3 focus:ring-sage-500 focus:border-sage-500 transition-all duration-200 bg-white shadow-sm"
        />
      </div>

      {/* 部位筛选 */}
      <div className="mb-3">
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
          <button
            type="button"
            onClick={() => handleSelectBodyPart('')}
            className={`shrink-0 h-10 px-4 rounded-full text-sm font-medium transition-colors ${
              activeBodyPart === ''
                ? 'bg-sage-600 text-white shadow-soft'
                : 'bg-white/70 text-sage-700 border border-sage-200 hover:bg-sage-50'
            }`}
          >
            全部部位
          </button>
          {meta.body_parts.map((bp) => (
            <button
              key={bp.body_part}
              type="button"
              onClick={() => handleSelectBodyPart(bp.body_part)}
              className={`shrink-0 h-10 px-4 rounded-full text-sm font-medium transition-colors ${
                activeBodyPart === bp.body_part
                  ? 'bg-sage-600 text-white shadow-soft'
                  : 'bg-white/70 text-sage-700 border border-sage-200 hover:bg-sage-50'
              }`}
            >
              {bp.body_part_zh || bp.body_part}
              <span className="ml-1 text-xs opacity-75">{bp.count}</span>
            </button>
          ))}
        </div>
      </div>

      {/* 器材筛选 */}
      <div className="mb-6">
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
          <button
            type="button"
            onClick={() => setActiveEquipment('')}
            className={`shrink-0 h-9 px-3 rounded-full text-xs font-medium transition-colors ${
              activeEquipment === ''
                ? 'bg-emerald-600 text-white'
                : 'bg-white/60 text-sage-600 border border-sage-200 hover:bg-sage-50'
            }`}
          >
            全部器材
          </button>
          {availableEquipments.map((eq) => (
            <button
              key={eq.equipment}
              type="button"
              onClick={() => setActiveEquipment(eq.equipment)}
              className={`shrink-0 h-9 px-3 rounded-full text-xs font-medium transition-colors ${
                activeEquipment === eq.equipment
                  ? 'bg-emerald-600 text-white'
                  : 'bg-white/60 text-sage-600 border border-sage-200 hover:bg-sage-50'
              }`}
            >
              {eq.equipment_zh || eq.equipment}
              <span className="ml-1 opacity-75">{eq.count}</span>
            </button>
          ))}
        </div>
      </div>

      {/* 加载/错误/空态 */}
      {loading && (
        <div className="text-center py-16 text-sage-500">
          <div className="inline-block w-8 h-8 border-2 border-sage-300 border-t-sage-600 rounded-full animate-spin mb-3"></div>
          <p>正在加载动作库...</p>
        </div>
      )}

      {!loading && error && (
        <div className="bg-red-50 border-l-4 border-red-400 text-red-700 px-4 py-3 rounded-r-lg text-sm">
          {error}
        </div>
      )}

      {!loading && !error && filteredExercises.length === 0 && (
        <div className="text-center py-16 text-sage-500">
          <p className="text-lg mb-2">没有找到匹配的动作</p>
          <p className="text-sm">试试更换部位、器材或搜索关键词</p>
        </div>
      )}

      {/* 动作网格 */}
      {!loading && !error && filteredExercises.length > 0 && (
        <>
          <p className="text-xs text-sage-500 mb-3">
            共 {filteredExercises.length} 个动作{hasMore ? `，已显示 ${visibleExercises.length} 个` : ''}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {visibleExercises.map((exercise) => (
              <button
                key={exercise.id}
                type="button"
                onClick={() => openDetail(exercise)}
                className="text-left bg-white/80 backdrop-blur-sm rounded-2xl shadow-soft border border-sage-100 overflow-hidden hover:shadow-zen hover:-translate-y-0.5 transition-all duration-300"
              >
                <div className="aspect-square bg-white flex items-center justify-center overflow-hidden">
                  <ImageWithFallback
                    src={exercise.image_path}
                    alt={exercise.name}
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                </div>
                <div className="p-3">
                  <h3 className="text-sm font-semibold text-sage-800 leading-snug line-clamp-2 min-h-[2.5rem]">
                    {exercise.name}
                  </h3>
                  <div className="mt-2 flex flex-wrap gap-1">
                    <span className="px-2 py-0.5 bg-sage-100 text-sage-700 rounded-full text-xs">
                      {exercise.body_part_zh || exercise.body_part}
                    </span>
                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs">
                      {exercise.equipment_zh || exercise.equipment}
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
          <LoadMoreButton hasMore={hasMore} remaining={remaining} onClick={loadMore} />
        </>
      )}

      {/* 详情弹窗（portal 到 body，避免被父级 transform/overflow 限制） */}
      {selectedId !== null && createPortal(
        <div className="fixed inset-0 z-50 overflow-y-auto overscroll-contain">
          <div
            className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm"
            onClick={closeDetail}
          ></div>

          <div className="flex min-h-full items-end sm:items-center justify-center sm:p-4">
            <div className="relative w-full max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden">
              <button
                type="button"
                onClick={closeDetail}
                aria-label="关闭"
                className="absolute top-3 right-3 z-20 bg-white/90 rounded-full p-2.5 shadow-md hover:bg-sage-50 active:bg-sage-100 transition-colors border border-sage-200"
              >
                <svg className="w-5 h-5 text-sage-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {detailLoading && (
                <div className="text-center py-20 text-sage-500">
                  <div className="inline-block w-8 h-8 border-2 border-sage-300 border-t-sage-600 rounded-full animate-spin mb-3"></div>
                  <p>加载动作详情...</p>
                </div>
              )}

              {!detailLoading && detail && detail.error && (
                <div className="p-6">
                  <div className="bg-red-50 border-l-4 border-red-400 text-red-700 px-4 py-3 rounded-r-lg text-sm">
                    {detail.error}
                  </div>
                </div>
              )}

              {!detailLoading && detail && !detail.error && (
                <div className="p-5 sm:p-6">
                  <h2 className="text-xl sm:text-2xl font-serif font-bold text-sage-800 pr-10">
                    {detail.name}
                  </h2>

                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <span className="px-2.5 py-1 bg-sage-100 text-sage-700 rounded-full text-xs font-medium">
                      {detail.body_part_zh || detail.body_part}
                    </span>
                    <span className="px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                      {detail.equipment_zh || detail.equipment}
                    </span>
                    <span className="px-2.5 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                      目标：{detail.target_zh || detail.target}
                    </span>
                  </div>

                  {/* GIF 动画（点开详情才加载） */}
                  <div className="mt-4 bg-white rounded-xl border border-sage-100 overflow-hidden flex items-center justify-center">
                    <ImageWithFallback
                      src={detail.gif_path}
                      alt={`${detail.name} 动作演示`}
                      className="w-full max-h-72 object-contain"
                    />
                  </div>

                  {/* 中文步骤 */}
                  {Array.isArray(detail.steps_zh) && detail.steps_zh.length > 0 && (
                    <div className="mt-4">
                      <h3 className="text-sm font-semibold text-sage-700 mb-2">动作步骤</h3>
                      <ol className="space-y-2">
                        {detail.steps_zh.map((step, index) => (
                          <li key={index} className="flex items-start gap-2.5 text-sm text-sage-700 leading-relaxed">
                            <span className="shrink-0 w-5 h-5 mt-0.5 bg-sage-600 text-white rounded-full flex items-center justify-center text-xs font-semibold">
                              {index + 1}
                            </span>
                            <span>{step}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {/* 次要肌肉 */}
                  {Array.isArray(detail.secondary_muscles) && detail.secondary_muscles.length > 0 && (
                    <div className="mt-4">
                      <h3 className="text-sm font-semibold text-sage-700 mb-2">协同肌肉</h3>
                      <div className="flex flex-wrap gap-1.5">
                        {detail.secondary_muscles.map((muscle) => (
                          <span key={muscle} className="px-2 py-0.5 bg-warm-100 text-warm-700 rounded-full text-xs">
                            {muscle}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {detail.attribution && (
                    <p className="mt-5 text-xs text-sage-400 text-center">{detail.attribution}</p>
                  )}

                  <div className="h-[env(safe-area-inset-bottom)] sm:hidden"></div>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ExerciseList;
